import React, { useEffect, useRef, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

type ItemNotasTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  /** ms para propagar al padre mientras se escribe (0 = solo al blur). */
  debounceMs?: number;
};

/**
 * Campo de notas por ítem con estado local: evita re-render del formulario
 * en cada tecla. Propaga al padre con debounce y al blur.
 */
const ItemNotasTextField: React.FC<ItemNotasTextFieldProps> = ({
  value,
  onChange,
  maxLength = 500,
  debounceMs = 200,
  inputProps,
  onFocus,
  onBlur,
  ...rest
}) => {
  const [localValue, setLocalValue] = useState(String(value ?? ''));
  const focusedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!focusedRef.current) {
      setLocalValue(String(value ?? ''));
    }
  }, [value]);

  useEffect(() => {
    if (!focusedRef.current || debounceMs <= 0) return;
    const handle = window.setTimeout(() => {
      const next = localValue.slice(0, maxLength);
      if (next !== String(valueRef.current ?? '')) {
        onChangeRef.current(next);
      }
    }, debounceMs);
    return () => window.clearTimeout(handle);
  }, [localValue, debounceMs, maxLength]);

  const commit = (raw: string) => {
    const next = raw.slice(0, maxLength);
    setLocalValue(next);
    if (next !== String(valueRef.current ?? '')) {
      onChangeRef.current(next);
    }
  };

  return (
    <TextField
      size="small"
      {...rest}
      value={localValue}
      onFocus={(e) => {
        focusedRef.current = true;
        onFocus?.(e);
      }}
      onChange={(e) => {
        setLocalValue(e.target.value.slice(0, maxLength));
      }}
      onBlur={(e) => {
        focusedRef.current = false;
        commit(localValue);
        onBlur?.(e);
      }}
      inputProps={{
        maxLength,
        ...inputProps,
      }}
    />
  );
};

export default ItemNotasTextField;
